<template>
  <!-- PIN variation — multi-box numeric control -->
  <div
    v-if="isPin"
    class="smardie-text-input smardie-text-input--pin"
    :class="{ 'smardie-text-input--error': error, 'smardie-text-input--disabled': disabled }"
    role="group"
    :aria-label="ariaLabel || '4-digit PIN'"
  >
    <input
      v-for="(digit, index) in pinDigits"
      :key="index"
      :ref="'digit-' + index"
      class="smardie-text-input__pin-box"
      :class="{ error: error }"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      maxlength="1"
      autocomplete="one-time-code"
      :value="digit"
      :disabled="disabled"
      :aria-label="'Digit ' + (index + 1)"
      @input="onPinBoxInput(index, $event)"
      @keydown="onPinKeydown(index, $event)"
      @paste="onPinPaste($event)"
      @focus="onPinFocus(index, $event)"
    />
  </div>

  <!-- Standard text input -->
  <div
    v-else
    class="ui fluid input smardie-text-input"
    :class="{ 'left icon': icon, disabled: disabled, error: error }"
  >
    <input
      :type="type"
      :placeholder="placeholder"
      :value="modelValue"
      :disabled="disabled"
      :readonly="readonly"
      :name="name"
      :id="inputId"
      :autocomplete="autocomplete"
      :step="step"
      :min="min"
      :max="max"
      :tabindex="tabindex"
      :class="{ error: error }"
      @input="onInput"
      @keyup="onKeyup"
      @keypress="$emit('keypress', $event)"
      @change="onChange"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <i v-if="icon" :class="icon + ' icon'" aria-hidden="true"></i>
  </div>
</template>

<script>
function digitsOnly (value) {
  return String(value == null ? '' : value).replace(/\D/g, '')
}

export default {
  name: 'TextInput',
  compatConfig: {
    COMPONENT_V_MODEL: false
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    /** `text` (default) or `pin` for multi-box numeric PIN entry */
    variant: {
      type: String,
      default: 'text',
      validator: function (value) {
        return ['text', 'pin'].indexOf(value) !== -1
      }
    },
    /** PIN length when variant="pin" */
    length: {
      type: Number,
      default: 4
    },
    type: {
      type: String,
      default: 'text'
    },
    placeholder: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    step: {
      type: [String, Number],
      default: undefined
    },
    min: {
      type: [String, Number],
      default: undefined
    },
    max: {
      type: [String, Number],
      default: undefined
    },
    tabindex: {
      type: [String, Number],
      default: undefined
    }
  },
  computed: {
    isPin: function () {
      return this.variant === 'pin'
    },
    pinDigits: function () {
      const clean = digitsOnly(this.modelValue).slice(0, this.length)
      const out = []
      for (let i = 0; i < this.length; i++) {
        out.push(clean.charAt(i) || '')
      }
      return out
    }
  },
  methods: {
    onInput: function (event) {
      this.$emit('update:modelValue', event.target.value)
    },
    onKeyup: function (event) {
      this.$emit('keyup', event)
    },
    onChange: function (event) {
      this.$emit('change', event)
    },
    emitPinValue: function (next) {
      const clean = digitsOnly(next).slice(0, this.length)
      this.$emit('update:modelValue', clean)
    },
    focusIndex: function (index) {
      const clamped = Math.max(0, Math.min(this.length - 1, index))
      this.$nextTick(() => {
        const ref = this.$refs['digit-' + clamped]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && typeof el.focus === 'function') {
          el.focus()
          if (typeof el.select === 'function') el.select()
        }
      })
    },
    onPinFocus: function (index, event) {
      if (event && event.target && typeof event.target.select === 'function') {
        event.target.select()
      }
      this.$emit('focus', { index: index, event: event })
    },
    onPinBoxInput: function (index, event) {
      const raw = event && event.target ? event.target.value : ''
      const typed = digitsOnly(raw)
      if (!typed) {
        const next = this.pinDigits.slice()
        next[index] = ''
        this.emitPinValue(next.join(''))
        return
      }
      const digit = typed.charAt(typed.length - 1)
      const next = this.pinDigits.slice()
      next[index] = digit
      this.emitPinValue(next.join(''))
      if (index < this.length - 1) this.focusIndex(index + 1)
    },
    onPinKeydown: function (index, event) {
      const key = event.key
      if (key === 'Backspace') {
        if (this.pinDigits[index]) {
          const next = this.pinDigits.slice()
          next[index] = ''
          this.emitPinValue(next.join(''))
        } else if (index > 0) {
          event.preventDefault()
          const next = this.pinDigits.slice()
          next[index - 1] = ''
          this.emitPinValue(next.join(''))
          this.focusIndex(index - 1)
        }
        return
      }
      if (key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusIndex(index - 1)
        return
      }
      if (key === 'ArrowRight' && index < this.length - 1) {
        event.preventDefault()
        this.focusIndex(index + 1)
        return
      }
      if (key && key.length === 1 && !/[0-9]/.test(key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
      }
    },
    onPinPaste: function (event) {
      event.preventDefault()
      const text = (event.clipboardData || window.clipboardData).getData('text')
      const clean = digitsOnly(text).slice(0, this.length)
      this.emitPinValue(clean)
      this.focusIndex(Math.min(clean.length, this.length - 1))
    },
    focus: function () {
      if (this.isPin) {
        const firstEmpty = this.pinDigits.findIndex((d) => !d)
        this.focusIndex(firstEmpty === -1 ? 0 : firstEmpty)
        return
      }
      const root = this.$el
      const input = root && root.querySelector ? root.querySelector('input') : null
      if (input && typeof input.focus === 'function') input.focus()
    }
  }
}
</script>
